import { ZoneStrategy } from '@ekkogmbh/apisdk';
import React, { useEffect, useMemo, useState } from 'react';
import { StrategyConfig } from '../Helper/Strategies';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { ListItemText, MenuItem, Stack } from '@mui/material';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { Settings } from '@mui/icons-material';
import { ListForm } from 'src/Common/Components/Forms/ListForm';
import { StyledSwitch } from 'src/Common/Components/Forms/StyledSwitch';

interface Props {
  optional?: boolean;
  strategy?: ZoneStrategy;
  label: string;
  onChange: (strategy?: ZoneStrategy) => void;
  configMap: Record<string, StrategyConfig[]>;
  defaultStrategy?: ZoneStrategy;
}
export default function ZoneStrategyFormSelect({
  optional,
  strategy,
  configMap,
  onChange,
  defaultStrategy,
  label,
}: Props): React.JSX.Element {
  const isOptional = useMemo(() => optional === true, [optional]);
  const configInputDefintions = useMemo(() => {
    if (strategy === undefined) {
      return [];
    }
    return configMap[strategy.name];
  }, [strategy?.name, configMap]);

  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (strategy === undefined && !isOptional && defaultStrategy) {
      onChange(defaultStrategy);
    }
  }, [isOptional, onChange, defaultStrategy]);

  /** ensures the config is closed when there are no inputs */
  useEffect(() => {
    if (configInputDefintions.length === 0 && isOpen) {
      setOpen(false);
    }
  }, [configInputDefintions.length, isOpen]);

  const configForm = useMemo(() => {
    if (strategy === undefined) {
      return <></>;
    }

    const { name, configuration } = strategy;

    // without the extra padding, the transition tends to clip the first input's label
    return (
      <Stack direction={'column'} spacing={1}>
        {configInputDefintions.map((c, i) => {
          if (c.list) {
            return (
              <ListForm
                key={i}
                label={c.label}
                items={(configuration[c.key] ?? c.default ?? []) as string[]}
                onChange={(itms) => onChange({ name, configuration: { ...configuration, [c.key]: itms } })}
                forbiddenCharacters={[',']}
                inputTooltip={c.tooltip}
              />
            );
          }

          if (c.options) {
            return (
              <StyledSelectField
                key={i}
                label={c.label}
                value={configuration[c.key] ?? c.default}
                onChange={(e) => {
                  onChange({ name, configuration: { ...configuration, [c.key]: e.target.value as string } });
                }}
                tooltip={c.tooltip}
              >
                {c.options.map((o, idx) => (
                  <MenuItem key={idx} value={o}>
                    <ListItemText>{o}</ListItemText>
                  </MenuItem>
                ))}
              </StyledSelectField>
            );
          } else if (c.variant === 'toggle') {
            const configValue = configuration[c.key] ?? c.default;

            let isSet = false;
            switch (typeof configValue) {
              case 'string':
                isSet = configValue === 'true';
                break;
              case 'boolean':
                isSet = configValue;
                break;
              default:
                break;
            }
            return (
              <StyledSwitch
                key={i}
                value={isSet}
                tooltip={c.tooltip}
                label={c.label}
                onChange={(v) => {
                  onChange({ name, configuration: { ...configuration, [c.key]: v ? 'true' : 'false' } });
                }}
              />
            );
          } else {
            return (
              <StyledTextField
                key={i}
                type={c.variant ?? 'text'}
                numeric={c.variant === 'number'}
                label={c.label}
                value={configuration[c.key] ?? c.default}
                helperText={c.hint}
                tooltip={c.tooltip}
                onChange={(e) => {
                  const value =
                    c.variant === 'number' && e.target.value !== ''
                      ? Math.max(0, parseInt(e.target.value))
                      : e.target.value;
                  onChange({ name, configuration: { ...configuration, [c.key]: value as string } });
                }}
              />
            );
          }
        })}
      </Stack>
    );
  }, [strategy, configInputDefintions]);

  return (
    <StyledSelectField
      label={label}
      value={strategy?.name ?? ''}
      onChange={(e) => {
        if (e.target.value === '') {
          onChange(undefined);
          setOpen(false);
        } else {
          const name = e.target.value as string;
          const configDefs = configMap[name];
          const defaultConfig = configDefs.reduce(
            (carry, def) => {
              if (def.default) {
                carry[def.key] = def.default;
              }
              return carry;
            },
            {} as ZoneStrategy['configuration'],
          );

          onChange({ name, configuration: defaultConfig });
          setOpen(configDefs.length > 0);
        }
      }}
      action={{
        icon: Settings,
        disabled: configInputDefintions.length === 0,
        onClick: () => {
          setOpen(!isOpen);
        },
      }}
      collapsible={{
        content: configForm,
        open: isOpen,
      }}
    >
      {isOptional && (
        <MenuItem value={''}>
          <ListItemText>{'-'}</ListItemText>
        </MenuItem>
      )}
      {Object.keys(configMap).map((name, i) => (
        <MenuItem key={i} value={name}>
          <ListItemText>{name}</ListItemText>
        </MenuItem>
      ))}
    </StyledSelectField>
  );
}
